<template>
  <b-overlay :show="showOverlay" rounded="sm">
    <div>
      <div class="d-flex align-items-start justify-content-between mb-2">
        <div>
          <h4 class="text-terciary mb-1">{{ title }}</h4>
          <p v-if="hint" class="text-terciary m-0">{{ hint }}</p>
        </div>

        <small v-if="maxLength" class="text-muted">
          {{ remainingChars }}
        </small>
      </div>

      <div class="inputWrapper">
        <b-form-textarea
          rows="3"
          no-resize
          v-model="text"
          :placeholder="placeholder"
          ref="TextRef"
          class="m-0 pl-4 pr-5 py-3 form-control-sm text-terciary font-weight-normal rounded"
          style="overflow:auto"
          :disabled="disabled || submitting"
        />
        <EmojiComponent v-on:append="appendEmoji($event)" />
      </div>

      <div class="py-3 d-flex align-items-center flex-wrap">
        <b-button
          class="m-0 p-0 btn shadow-none"
          style="background:transparent;border:none"
          @click="pickFile"
          :disabled="disabled || submitting"
        >
          <b-icon-camera class="text-muted mr-3 mb-3" font-scale="1.5" />
        </b-button>

        <input
          type="file"
          style="display:none"
          ref="InputFile"
          accept="video/mp4,video/x-m4v,video/*,image/*"
          @change="uploadFile"
        />

        <div v-if="selectedFile && fileUploaded" class="d-flex align-items-center mb-2">
          <span class="text-primary mr-2">{{ selectedFile.name }}</span>
          <b-button
            class="m-0 p-0 btn shadow-none"
            style="background:transparent;border:none"
            @click="clearAttachment"
            :disabled="disabled || submitting"
          >
            <b-icon-x-circle class="text-muted" font-scale="1.2" />
          </b-button>
        </div>

        <b-button
          class="px-5 py-2 ml-auto rounded mb-2"
          variant="primary"
          :disabled="isPublishDisabled"
          v-if="!submitting"
          @click="submit"
        >
          {{ publishLabel }}
        </b-button>

        <b-spinner small v-if="submitting" class="ml-auto mb-2" />
      </div>
    </div>
  </b-overlay>
</template>

<script>
import axios from "axios"
import EmojiComponent from "@/components/Checkout_Team/EmojiComponent.vue"
import { BIconCamera, BIconXCircle } from "bootstrap-vue"

export default {
  name: "AdminCommunicationComposer",
  components: { EmojiComponent, BIconCamera, BIconXCircle },
  props: {
    title: { type: String, default: "" },
    hint: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    publishLabel: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    minLength: { type: Number, default: 1 },
    maxLength: { type: Number, default: 500 },
  },
  data() {
    return {
      showOverlay: false,
      submitting: false,
      text: "",
      selectedFile: null,
      media_file_id: null,
      fileUploaded: false,
    }
  },
  computed: {
    trimmedText() {
      return (this.text || "").trim()
    },
    remainingChars() {
      if (!this.maxLength) return ""
      return `${this.trimmedText.length}/${this.maxLength}`
    },
    isPublishDisabled() {
      const hasText = this.trimmedText.length >= this.minLength
      const hasMedia = !!this.media_file_id
      const tooLong = this.maxLength && this.trimmedText.length > this.maxLength
      return this.disabled || this.submitting || (!hasText && !hasMedia) || tooLong
    },
  },
  methods: {
    pickFile() {
      this.$refs.InputFile.click()
    },

    clearAttachment() {
      this.selectedFile = null
      this.media_file_id = null
      this.fileUploaded = false
      if (this.$refs.InputFile) this.$refs.InputFile.value = ""
    },

    appendEmoji(emoji) {
      const cursorPos = this.$refs.TextRef?.selectionStart ?? this.text.length
      this.text = this.text.slice(0, cursorPos) + emoji + this.text.slice(cursorPos)
    },

    async uploadFile(event) {
      const input = event.target
      if (!input.files || !input.files[0]) return

      this.selectedFile = input.files[0]

      try {
        const response = await axios.post("/mediaobject", {
          filename: this.selectedFile.name,
          context_type: "COMM",
        })

        if (response.status === 200) {
          this.fileUploaded = true
          this.media_file_id = response.data?.data?.media_file_id
          const saveUrl = response.data?.data?.save_url
          await this.uploadFileToS3(saveUrl)
        }
      } catch ({ response }) {
        this.$store.dispatch("addToastMessage", {
          type: "danger",
          text: response?.data?.error_message || this.$t("error_occurred"),
        })
        this.clearAttachment()
      }
    },

    async uploadFileToS3(url) {
      this.showOverlay = true
      try {
        await fetch(url, {
          method: "PUT",
          body: this.selectedFile,
          headers: { "Content-Type": this.selectedFile.type },
        })
      } catch (e) {
        this.$store.dispatch("addToastMessage", {
          type: "danger",
          text: this.$t("error_occurred"),
        })
        this.clearAttachment()
      } finally {
        this.showOverlay = false
      }
    },

    async submit() {
      if (this.isPublishDisabled) return
      this.submitting = true
      try {
        await this.$emit("submit", {
          text: this.trimmedText,
          media_file_id: this.media_file_id || null,
        })

        // Reset only after parent succeeds
        this.text = ""
        this.clearAttachment()
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.inputWrapper {
  position: relative;
}
</style>